import { clsx } from 'clsx'
import { useLatestCallback } from 'hook/useLatestCallback'
import { ForwardedRef, forwardRef, MouseEvent, ReactNode, useState } from 'react'
import { opt } from 'store/opt'
import { Gap, getGapStyle, getPaddingStyle, Padding, styles } from 'util/style'
import css from './Button.module.scss'

type Props = {
  compact?: boolean
  children: ReactNode
  disabled?: boolean
  action?(e: MouseEvent): unknown
  text?: boolean
  link?: boolean
  outline?: boolean
  invert?: boolean
  gray?: boolean
  primary?: boolean
  danger?: boolean
  active?: boolean
  className?: string
  xl?: boolean
  large?: boolean
  wide?: boolean
  icon?: ReactNode
  height?: string | number
  gap?: Gap
  pad?: Padding
  simple?: boolean
  left?: boolean
}

function buildStyle(props: Props) {
  const { height, pad } = props
  const gap = getGapStyle(props.gap)
  const padding = getPaddingStyle(pad)
  return styles({ height, ...gap, ...padding })
}

export const Button = forwardRef(
  (props: Props, ref: ForwardedRef<HTMLButtonElement>) => {
    const {
      action,
      active,
      children,
      compact,
      disabled,
      icon,
      invert,
      gray,
      large,
      link,
      outline,
      primary,
      danger,
      text,
      wide,
      xl,
      simple,
      left,
    } = props
    const [busy, setBusy] = useState(false)
    const type = primary
      ? css.primary
      : danger
        ? css.danger
        : invert
          ? css.invert
          : outline
            ? css.outline
            : text || link
              ? css.text
              : css.default

    const className = clsx(
      css.control,
      css.button,
      type,
      active === true && css.active,
      active === false && css.inactive,
      opt.square && css.square,
      compact && css.compact,
      gray && css.gray,
      (xl || large) && css.xl,
      wide && css.wide,
      icon && css.withIcon,
      simple && css.simple,
      left && css.left,
      props.className,
    )
    const style = buildStyle(props)

    const onClick = useLatestCallback(async (event: MouseEvent) => {
      if (action && event.button === 0 && event.detail === 1) {
        const promise = action(event)
        if (promise instanceof Promise) {
          setBusy(true)
          await promise
          setBusy(false)
        }
      }
    })

    return (
      <button
        ref={ref}
        className={className}
        style={style}
        disabled={disabled || busy}
        onClick={onClick}
      >
        {icon}
        {icon ? <span>{children}</span> : children}
      </button>
    )
  },
)
